import React, { useRef } from "react";
import FemcareLogo from "./FemcareLogo";
import { useSpotlight } from "../lib/motion";

/**
 * Cinematic Editorial page hero: aurora + grain + F-coin + editorial headline.
 * Reusable across all inner pages.
 *
 * Props:
 *  - overline: small uppercase tracking-wide label
 *  - title: <> React node (allow <br/> and <span>)
 *  - subtitle: string (optional)
 *  - children: any extra hero content (buttons, chips…)
 *  - dark: use aurora-dark theme
 *  - size: 'sm' | 'md' | 'lg' (default 'md')
 *  - testId: root data-testid
 */
export default function PageHero({
    overline,
    title,
    subtitle,
    children,
    dark = false,
    size = "md",
    testId,
}) {
    const ref = useRef(null);
    useSpotlight(ref);

    const pad = size === "sm" ? "pt-16 pb-12" : size === "lg" ? "pt-28 pb-24" : "pt-20 pb-16";
    const h1 =
        size === "sm"
            ? "text-4xl sm:text-5xl lg:text-6xl"
            : size === "lg"
            ? "text-5xl sm:text-6xl lg:text-7xl xl:text-8xl"
            : "text-4xl sm:text-5xl lg:text-6xl";

    return (
        <section ref={ref} className="relative overflow-hidden spotlight" data-testid={testId}>
            <div className={`aurora ${dark ? "aurora-dark" : ""} grain`} aria-hidden="true" />
            <div className={`max-w-6xl mx-auto px-6 lg:px-12 ${pad} relative`}>
                <div className="flex items-center gap-4 mb-8 reveal">
                    <div className="f-coin">
                        <FemcareLogo className="w-9 h-9" />
                    </div>
                    {overline && (
                        <span
                            className={`text-xs uppercase tracking-[0.35em] font-medium ${
                                dark ? "text-femcare-pink" : "text-femcare-accent"
                            }`}
                        >
                            {overline}
                        </span>
                    )}
                </div>
                <h1
                    className={`editorial-headline ${h1} leading-none mb-6 reveal reveal-delay-1 ${
                        dark ? "text-white" : "text-femcare-purple"
                    }`}
                >
                    {title}
                </h1>
                {subtitle && (
                    <p
                        className={`text-lg md:text-xl max-w-2xl leading-relaxed reveal reveal-delay-2 ${
                            dark ? "text-pink-100/80" : "text-femcare-purple/70"
                        }`}
                    >
                        {subtitle}
                    </p>
                )}
                {children && <div className="mt-8 reveal reveal-delay-3">{children}</div>}
            </div>
        </section>
    );
}
